import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import { ReactElement } from 'react';
import Close from '@material-ui/icons/Close';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import { CardItem } from './types/support';
import { ContentCardProps } from './types/content-props';
import { AppBarComponent } from './app-bar-component';
import { ModalMainContent } from './modal-content-component';
import { PotentialDuplicateNotice } from './potential-duplicate-notice';
import { FontFamily } from '@principal-developer-portal/ui';
import { FRAMEWORK_IDS } from './types/constant';
import { ExceptionCardInfo } from './types/exception';
import {
  MarkAsRemediatedButton,
  MarkAsRemediatedButtonProps,
} from './mark-as-remediated-button';

export interface InsightsIssueActionProps {
  appbar: {
    title: string;
    subtitle: string;
    timeStamp: string;
    fullDescription?: string;
    foundDate?: string;
    remediatedAt?: string;
    redoRequired?: boolean;
    redoAt?: string;
    redoReason?: string;
    zeroDay?: boolean;
    removalTimingHref?: string;
  };
  modalContent: {
    contentProps: ContentCardProps;
  };
  supportCard: {
    cardItems: CardItem[];
  };
  onClose: () => void;
  open: boolean;
  exceptionCard: {
    exceptionCardItems?: ExceptionCardInfo[];
  };
  loading?: boolean;
  potentialDuplicate?: boolean;
  remediationProps?: MarkAsRemediatedButtonProps;
}
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const PendingPolicyInfo = () => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <InfoOutlined color="primary" fontSize="large" />
    <span
      style={{
        fontFamily: FontFamily.Bold,
        fontWeight: 400,
        fontSize: '24px',
        lineHeight: '36px',
        letterSpacing: '0px',
      }}
    >
      Pending policy, not yet required
    </span>
  </div>
);

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: '20px 40px 40px 40px',
    overflowY: 'auto',
    overflowX: 'hidden',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-track': {
      background: theme.palette.type === 'dark' ? '#2D2D2D' : '#E8E8E8',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-thumb': {
      background: '#9D9EA4',
      borderRadius: '50px',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '16px',
    },
  },
}));

export const WIZ_FRAMEWORK_IDS: string[] = [
  FRAMEWORK_IDS.WIZ_ISR_STAGING_FRAMEWORK_SANDBOX,
  FRAMEWORK_IDS.WIZ_ISR_STAGING_FRAMEWORK,
];

// eslint-disable-next-line max-lines-per-function
export const InsightsIssueAction = (
  props: InsightsIssueActionProps,
): ReactElement => {
  const classes = useStyles();
  const {
    onClose,
    open,
    appbar,
    modalContent,
    supportCard,
    exceptionCard,
    loading,
    potentialDuplicate,
    remediationProps,
  } = props;

  const { frameworkIds, dueDateText } = modalContent.contentProps;

  const isFrameworkMatched =
    frameworkIds && WIZ_FRAMEWORK_IDS.some(id => frameworkIds.includes(id));

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{
        style: { maxWidth: '80%' },
      }}
      maxWidth="md"
      data-testid="insights-issue-action-dialog"
    >
      <DialogTitle data-testid="insights-issue-action-dialog-title">
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
            data-testid="insights-issue-action-dialog-close-button"
          >
            <Close />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent
        className={classes.dialogContent}
        data-testid="insights-issue-action-dialog-content"
      >
        <Grid
          container
          spacing={3}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item xs={12} data-testid="insights-issue-action-appbar">
            <AppBarComponent
              title={appbar.title ?? 'Unknown issue'}
              subtitle={appbar.subtitle ?? ''}
              timeStamp={appbar.timeStamp}
              fullDescription={appbar.fullDescription}
              foundDate={appbar.foundDate}
              remediatedAt={appbar.remediatedAt}
              redoRequired={appbar.redoRequired}
              redoAt={appbar.redoAt}
              redoReason={appbar.redoReason}
              zeroDay={appbar.zeroDay}
              removalTimingHref={appbar.removalTimingHref}
            />
          </Grid>
          {potentialDuplicate && (
            <Grid item xs={12} data-testid="insights-issue-action-duplicate">
              <PotentialDuplicateNotice />
            </Grid>
          )}
          {remediationProps && (
            <Grid item xs={12} data-testid="insights-issue-action-remediation">
              <MarkAsRemediatedButton {...remediationProps} />
            </Grid>
          )}
          {isFrameworkMatched &&
            !dueDateText?.toLowerCase().includes('staging: planned active') && (
              <Grid
                item
                xs={12}
                data-testid="insights-issue-action-content-header"
              >
                <PendingPolicyInfo />
              </Grid>
            )}
          <Grid item xs={12} data-testid="insights-issue-action-content">
            <ModalMainContent
              contentDetails={modalContent.contentProps}
              cardItems={supportCard.cardItems}
              exceptionItems={exceptionCard?.exceptionCardItems}
              isFrameworkMatched={Boolean(isFrameworkMatched)}
              loading={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
